import { useCallback, useMemo, useState } from 'react';

import { Offer } from 'common/contracts';
import dayjs from 'dayjs';

interface IUseRewardCalendarFiltersProps {
  offers: Offer[];
  externalIdFilter: string;
  setExternalIdFilter: (value: string) => void;
  nameFilter: string;
  setNameFilter: (value: string) => void;
}

interface IUseRewardCalendarFiltersReturn {
  statusFilter: string[];
  offerDesignFilter: string[];
  createdAtFilter: string;
  hasAnyFilter: boolean;
  filteredOffers: Offer[];
  handleStatusChange: (value: string) => void;
  handleOfferDesignChange: (value: string) => void;
  handleCreatedAtChange: (value: string) => void;
  handleClearFilters: () => void;
}

export const useRewardCalendarFilters = ({
  offers,
  externalIdFilter,
  setExternalIdFilter,
  nameFilter,
  setNameFilter
}: IUseRewardCalendarFiltersProps): IUseRewardCalendarFiltersReturn => {
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [offerDesignFilter, setOfferDesignFilter] = useState<string[]>([]);
  const [createdAtFilter, setCreatedAtFilter] = useState('');

  const handleStatusChange = useCallback((value: string) => {
    setStatusFilter((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  }, []);

  const handleOfferDesignChange = useCallback((value: string) => {
    setOfferDesignFilter((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  }, []);

  const handleCreatedAtChange = useCallback((value: string) => {
    setCreatedAtFilter(value);
  }, []);

  const hasAnyFilter =
    !!externalIdFilter ||
    !!nameFilter ||
    statusFilter.length > 0 ||
    offerDesignFilter.length > 0 ||
    !!createdAtFilter;

  const handleClearFilters = useCallback(() => {
    setExternalIdFilter('');
    setNameFilter('');
    setStatusFilter([]);
    setOfferDesignFilter([]);
    setCreatedAtFilter('');
  }, [setExternalIdFilter, setNameFilter]);

  const filteredOffers = useMemo(() => {
    let result = offers;

    const lowerExternalId = externalIdFilter.toLowerCase();
    if (lowerExternalId) {
      result = result.filter((offer: Offer) =>
        offer.publisherOfferId.toLowerCase().includes(lowerExternalId)
      );
    }

    const lowerName = nameFilter.toLowerCase();
    if (lowerName) {
      result = result.filter((offer: Offer) =>
        offer.name.toLowerCase().includes(lowerName)
      );
    }

    if (statusFilter.length > 0) {
      result = result.filter((offer: Offer) => {
        const status = offer.active ? 'active' : 'inactive';
        return statusFilter.includes(status);
      });
    }

    if (offerDesignFilter.length > 0) {
      result = result.filter((offer: Offer) => {
        const designIds = [
          offer.offerUiId,
          offer.offerUi?.offerUiId,
          offer.offerUi?._id
        ].filter((id): id is string => Boolean(id));
        return designIds.some((id) => offerDesignFilter.includes(id));
      });
    }

    if (createdAtFilter) {
      const filterDate = dayjs(createdAtFilter);
      if (filterDate.isValid()) {
        result = result.filter((offer: Offer) => {
          if (!offer.createdAt) return false;
          return dayjs(offer.createdAt).isSame(filterDate, 'day');
        });
      }
    }

    return result;
  }, [
    offers,
    externalIdFilter,
    nameFilter,
    statusFilter,
    offerDesignFilter,
    createdAtFilter
  ]);

  return {
    statusFilter,
    offerDesignFilter,
    createdAtFilter,
    hasAnyFilter,
    filteredOffers,
    handleStatusChange,
    handleOfferDesignChange,
    handleCreatedAtChange,
    handleClearFilters
  };
};
